import React from 'react'

import { BackArrow, Share } from '@cinch/oxd-components/icons/src'
import Image from '@cinch/oxd-components/image/src'
import JargonSummary from '@cinch/oxd-components/jargon-summary/src'
import Link, { Appearance, Size } from '@cinch/oxd-components/link/src'
import Title from '@cinch/oxd-components/page-title/src'
import RichText from '@cinch/oxd-components/rich-text/src'
import SVG from '@cinch/oxd-components/svg/src'
import Layout from '@cinch/oxd-layouts/regular/src'
import tokens from '@cinch/oxd-utils/tokens/src/tokens'

import KeywordList from './components/keywords.component'

import { Env, getEnv } from '@cinch/oxd-utils/env/src'

import * as styles from './jargon.module.css'

interface JargonPageProps {
  id: string
  term: string
  fullTerm: string
  question: string
  definition: string
  keywords?: string[]
  relatedItems?: Array<Record<'id' | 'title' | 'subtitle', string>>
  image: any
}

const siteUrl = getEnv(Env.SiteUrl)

export const HeaderLink: React.FC<any> = ({ icon, children, ...props }) => (
  <Link size={Size.mini} appearance={Appearance.tertiaryButton} {...props}>
    <SVG children={icon} className={styles.icon} size={tokens.customProperties.spacing.m} hasGradient={true} />
    {children}
  </Link>
)

const JargonPage: React.FC<JargonPageProps> = ({
  id,
  term,
  fullTerm,
  question,
  definition,
  keywords = [],
  relatedItems = [],
  image,
}) => {
  return (
    <Layout>
      <div className={styles.header}>
        <HeaderLink icon={BackArrow} to={'/jargon'}>
          All Jargon
        </HeaderLink>
        <HeaderLink
          icon={Share}
          to={`mailto:?subject=${question} - Cinch&body=${siteUrl}/jargon/${id}`}
          external={true}
        >
          Share by Email
        </HeaderLink>
      </div>
      <h1 className={styles.fullTerm}>
        {fullTerm}
        {term && ` (${term})`}
      </h1>
      {keywords.length > 0 && (
        <div className={styles.keywords}>
          <span>Keywords</span>
          <KeywordList items={keywords} />
        </div>
      )}
      <article className={styles.article}>
        <div className={styles.content}>
          <h2 className={styles.question}>{question}</h2>
          <RichText>{definition}</RichText>
        </div>
        {!!image && (
          <div className={styles.imageWrapper}>
            {image.l ? (
              <picture>
                <source srcSet={image.s.url} media={tokens.customMedia.m} />
                <source srcSet={image.m.url} media={tokens.customMedia.s} />
                <img className={styles.image} alt={image.alt} src={image.m.url} />
              </picture>
            ) : (
              <figure>
                <Image className={styles.image} alt={image.alt} src={image.url} />
              </figure>
            )}
          </div>
        )}
      </article>
      {relatedItems.length > 0 && (
        <>
          <Title element="h2">Related Jargon</Title>
          <div className={styles.related}>
            {relatedItems.map((summaryProps, index) => (
              <JargonSummary key={summaryProps.id + index} {...summaryProps} />
            ))}
          </div>
        </>
      )}
    </Layout>
  )
}

export default JargonPage
